/**
 * `createApiRunsSource` — the `pipeline-mgmt-api` *run jobs* port over the shared
 * `ApiClient`.
 *
 * A run job is the async, monitorable batch path: it describes WHERE a batch of
 * transactions comes from (an ingest `source`) and runs each one through a
 * pipeline. Because a real ingest ("the latest N txs involving this address", a
 * set of fixed tx hashes) runs far longer than API Gateway's request edge allows,
 * the backend records the job + enqueues it; an SQS worker drains it and rolls
 * each tx's outcome back onto the job. This adapter exposes a camelCase domain
 * and a `poll(id)` helper the run console calls on an interval until the job
 * reaches a terminal state. Canonical `/runs/*` paths are mapped to
 * `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { outcomeFromWire, type BodyScope, type InjectionOutcome } from "@authe/fe-commons/pipeline";
import {
  asNullableString,
  asNumber,
  asRecord,
  asString,
  asStringList,
  isRecord,
  rowsAt,
  rowsFromWire,
} from "./wire";

/** Where a run job sources its transactions (mirrors backend `RunSourceKind`). */
export type RunSourceKind =
  | "inline"
  | "latest"
  | "tx_hashes"
  | "tx_specs"
  | "block_range"
  | "pair_swaps"
  | "wallet_swaps"
  | "token_transfers";

/**
 * Event kinds a `pair_swaps` run can be narrowed to (mirrors the provider's
 * `PairSwapTransactionType`). Spellings are case-sensitive on the wire.
 */
export const PAIR_SWAP_TRANSACTION_TYPES = [
  "buy",
  "sell",
  "addLiquidity",
  "removeLiquidity",
] as const;

export type PairSwapTransactionType = (typeof PAIR_SWAP_TRANSACTION_TYPES)[number];

/** One leg of a multi-chain `tx_specs` ingest: a tx hash on a specific chain. */
export interface RunTxSpec {
  chainId: string;
  txHash: string;
}

/** Lifecycle of a run job (mirrors backend `RunJobStatus`). */
export type RunJobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

/** A run job is finished once it reaches one of these (stop polling). */
export const TERMINAL_RUN_STATUSES: ReadonlySet<RunJobStatus> = new Set<RunJobStatus>([
  "succeeded",
  "failed",
  "cancelled",
]);

export function isTerminalRunStatus(status: RunJobStatus): boolean {
  return TERMINAL_RUN_STATUSES.has(status);
}

/**
 * How long past its lease a `running` job may go before it is called stalled.
 *
 * The recovery dispatcher looks for expired leases once a minute and a new
 * worker re-claims the job (renewing the lease), so a recoverable job is back
 * inside this window. The rest is clock skew between this browser and the
 * worker.
 */
export const STALLED_RUNNING_GRACE_SECONDS = 120;

/**
 * How long past its due time a re-queued job may wait before it is called
 * stalled — longer, because `queued` after a claim is usually on purpose.
 *
 * A worker that runs out of time parks the rest of its batch back to `queued`
 * and re-enqueues it; a failed ingest retries through `queued` after a backoff
 * of up to 900s (`recovery_after`). Measured from the later of the old lease and
 * that backoff, then one more worker lifetime (900s) on top.
 */
export const STALLED_QUEUED_GRACE_SECONDS = 900;

/**
 * Whether the worker holding this run stopped without recording an outcome.
 *
 * A worker claims a job with a lease and renews it on every claim. When the
 * process dies (the 900s Lambda wall, OOM) and the queue's redelivery is lost,
 * the row keeps saying `running` for ever — seen on 2026-09-16 on a Ronin
 * drainer ingest. Status cannot tell that row from a live one; the lease can.
 *
 * Never true for a run with no lease: one no worker has claimed yet is simply
 * waiting, and one recorded before leases existed has no evidence either way.
 * `nowMs` is a parameter so callers decide which clock a verdict is taken on.
 */
export function isStalledRun(
  job: Pick<RunJobSummary, "status" | "leaseExpiresAt" | "recoveryAfter">,
  nowMs: number,
): boolean {
  if (job.leaseExpiresAt == null) return false;
  const now = nowMs / 1000;
  if (job.status === "running") return now > job.leaseExpiresAt + STALLED_RUNNING_GRACE_SECONDS;
  if (job.status === "queued") {
    const due = Math.max(job.leaseExpiresAt, job.recoveryAfter ?? 0);
    return now > due + STALLED_QUEUED_GRACE_SECONDS;
  }
  return false;
}

/**
 * Whether a worker may still move this run forward — the one test for "keep
 * polling" and "N still running". A stalled run is neither terminal nor in flight.
 */
export function isRunInFlight(
  job: Pick<RunJobSummary, "status" | "leaseExpiresAt" | "recoveryAfter">,
  nowMs: number,
): boolean {
  return !isTerminalRunStatus(job.status) && !isStalledRun(job, nowMs);
}

/**
 * Lifecycle rank — a run only ever moves forward: queued (0) → running (1) →
 * terminal (2). Used to reject stale reads that would regress a job.
 */
export function runStatusRank(status: RunJobStatus): number {
  switch (status) {
    case "queued":
      return 0;
    case "running":
      return 1;
    default:
      return 2; // succeeded | failed | cancelled
  }
}

/**
 * Whether `next` was written by a later worker invocation than `prev` (1), an
 * earlier one (-1), or the same (0).
 *
 * Both counters only ever grow over a run's life: `resume_count` when a worker
 * runs out of time and parks the rest of the batch back to `queued`, and
 * `recovery_attempts` when a failed ingest retries through `queued` or a new
 * worker claims an expired lease. Mixed directions cannot come from real writes,
 * so they are read as stale.
 */
function compareRunInvocation(prev: RunJobSummary, next: RunJobSummary): number {
  const resumed = (next.resumeCount ?? 0) - (prev.resumeCount ?? 0);
  const recovered = (next.recoveryAttempts ?? 0) - (prev.recoveryAttempts ?? 0);
  if (resumed < 0 || recovered < 0) return -1;
  return resumed > 0 || recovered > 0 ? 1 : 0;
}

/**
 * Reconcile two views of the SAME run, keeping status monotonic.
 *
 * `pipeline-mgmt-api` runs on a multi-region DynamoDB global table, so reads can
 * land on a replica that hasn't caught up to the worker's last write — making a
 * finished run momentarily read back as `queued`/`running`. We treat a
 * lower-ranked incoming status as a stale replica read and keep the more
 * advanced view; monotonic counters (and a once-known total) likewise never
 * regress. This stops the run list from "flapping" between statuses.
 *
 * Monotonic per worker INVOCATION, not over the run's whole life. A long run
 * goes `running` → `queued` → `running` as it hands its remaining transactions
 * to a fresh invocation, and a retried ingest does the same. Rejecting that
 * `queued` kept the previous invocation's `running` view — and its expired
 * lease, which {@link isStalledRun} would then report as a worker that stopped.
 * Nothing reopens a finished run, so a terminal view is never given up.
 */
export function mergeRunJob(prev: RunJobSummary | undefined, next: RunJobSummary): RunJobSummary {
  if (!prev || prev.id !== next.id) return next;
  const invocation = compareRunInvocation(prev, next);
  if (invocation < 0) return prev;
  if (runStatusRank(next.status) < runStatusRank(prev.status)) {
    if (invocation === 0 || isTerminalRunStatus(prev.status)) return prev;
  }
  return {
    ...next,
    totalCount: Math.max(next.totalCount, prev.totalCount),
    processedCount: Math.max(next.processedCount, prev.processedCount),
    failedCount: Math.max(next.failedCount, prev.failedCount),
    producedTx: next.producedTx.length >= prev.producedTx.length ? next.producedTx : prev.producedTx,
    finishedAt: next.finishedAt ?? prev.finishedAt,
    error: next.error ?? prev.error,
    // Written once by the worker at the end of ingest — a stale replica read
    // that predates that write must not blank the only account of why the run
    // found nothing.
    ingestReport: next.ingestReport ?? prev.ingestReport,
    // Written once, when the worker starts the batch — a lagging replica read
    // must not blank what the run applied.
    appliedConfiguration: next.appliedConfiguration ?? prev.appliedConfiguration,
    // Immutable submit-time provenance — never let a lagging/partial read blank it.
    deploymentColour: next.deploymentColour ?? prev.deploymentColour,
    applicationVersion: next.applicationVersion ?? prev.applicationVersion,
    qaConfig: next.qaConfig ?? prev.qaConfig,
  };
}

/** A single transaction's outcome within a run (the run console renders + links from these). */
export interface ProducedTx {
  pipelineRunId?: string | null;
  canonicalTxId?: string | null;
  txHash?: string | null;
  status: string;
  error?: string | null;
}

export interface RunJobSummary {
  id: string;
  pipelineId: string;
  /**
   * The saved revision of the pipeline this run executes — resolved at
   * admission, so a later publish does not change what an already-queued run
   * does. `null` for a system preset (code, no revisions) and older runs.
   */
  pipelineVersion: number | null;
  sourceKind: RunSourceKind;
  chainId?: string | null;
  provider: string;
  count: number;
  /**
   * The address the ingest was scoped to: the contract/wallet for `latest` and
   * `block_range`, or the DEX pool for `pair_swaps`.
   */
  address?: string | null;
  selector?: string | null;
  onlyMatchingSelector: boolean;
  /** `pair_swaps`: the comma-separated event kinds the pair feed was filtered to. */
  transactionTypes?: string | null;
  txHashes: string[];
  /** `tx_specs`: the multi-chain `{chainId, txHash}` legs (cross-chain grouping). */
  txSpecs: RunTxSpec[];
  fromBlock?: number | null;
  toBlock?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  /** Whether the window above was a request or a fallback — see `RunSourceInput`. */
  windowMode?: "exact" | "only_when_large" | null;
  targetTier?: string | null;
  storeBody: boolean;
  /** Per-run override of the body storage scope (`customer`/`org`/`tenant`/`user`); null inherits the pipeline's. */
  bodyScope?: BodyScope | null;
  /** Force-reprocess: re-ran every step ignoring the idempotency cache and overwrote stored rows/bodies. */
  overwrite: boolean;
  status: RunJobStatus;
  requestedBy?: string | null;
  startedAt?: number | null;
  finishedAt?: number | null;
  createdAt?: number | null;
  /**
   * When the worker that last claimed this run stops holding it (unix seconds).
   *
   * Renewed on every claim. `null` on a run no worker has claimed yet, and on
   * runs recorded before leases existed — neither says anything about a stall.
   */
  leaseExpiresAt?: number | null;
  /** How many times the run handed its remaining transactions to a fresh invocation. */
  resumeCount?: number;
  /** How many times the run was retried or re-claimed after its worker stopped. */
  recoveryAttempts?: number;
  /** A retried `queued` run is not claimable before this (unix seconds). */
  recoveryAfter?: number | null;
  totalCount: number;
  processedCount: number;
  failedCount: number;
  error?: string | null;
  /**
   * Blue/green lane this run's child runs executed on: `blue` (latest deploy —
   * a QA/blue caller) or `green` (live release). `null` on runs recorded before
   * this was tracked. The console badges a blue run so it's clear it didn't run
   * on the live green release.
   */
  deploymentColour?: string | null;
  /** Published Lambda version of the edge that enqueued this run (build provenance). */
  applicationVersion?: string | null;
  /** Snapshot of the caller's blue/green routing config at submit time — present only for QA runs. */
  qaConfig?: Record<string, unknown> | null;
  /** Only populated on the single-job (`get`/`poll`) fetch, not the list. */
  producedTx: ProducedTx[];
  /**
   * What the ingest actually resolved, and why it produced nothing when it did.
   *
   * A run that fetches zero transactions SUCCEEDS, so `status` and `error` say
   * nothing about it and the counts say only "0". This is the difference
   * between a correct empty run and a window that misses the activity — which
   * are the same row, and opposite next actions.
   */
  ingestReport: IngestReport | null;
  /**
   * What `/analytics/protocols` contributed to this run, resolved when the
   * worker started it: the protocol overrides and confidence settings it
   * inherited per rung, and every stored override it had to IGNORE.
   *
   * `null` on runs recorded before the worker kept it — which means "unknown",
   * and must never be rendered as "nothing was overridden".
   */
  appliedConfiguration: AppliedConfiguration | null;
}

/** One protocol override a run applied. */
export interface AppliedProtocolOverride {
  protocolId: string;
  /** The stored rungs that supplied it, least specific first (`customer`, `org`, `tenant`, `user`). */
  rungs: string[];
  /** Whether the pipeline definition's own config patched it too (applied on top). */
  fromPipeline: boolean;
  /** The definition fields the effective patch sets. */
  fields: string[];
}

/** A stored override the run discarded, and why. */
export interface IgnoredProtocolOverride {
  protocolId: string;
  rung: string;
  reason: string;
}

/** An applied confidence setting nothing evaluates, and why. */
export interface InertConfidenceSetting {
  path: string;
  rung: string;
  reason: string;
}

/** The run's record of the per-scope configuration it resolved at start. */
export interface AppliedConfiguration {
  protocols: {
    /** `false` when the run could not resolve its overrides at all. */
    primed: boolean;
    /** `false` when the stored overrides could not be READ — the run decoded from its pipeline config only. */
    available: boolean;
    overrides: AppliedProtocolOverride[];
    ignored: IgnoredProtocolOverride[];
  };
  confidence: {
    primed: boolean;
    available: boolean;
    /** Stored rungs that authored anything. */
    rungs: string[];
    /** Every setting a stored rung set, with the rung whose value won. */
    settings: { path: string; rung: string }[];
    /** Settings the pipeline definition's own config pins (applied on top). */
    fromPipeline: string[];
    /** Stored settings the resolver flagged (typo'd domain, unknown action) — saved, doing nothing. */
    problems: string[];
    /**
     * Settings the run applied that NOTHING evaluates (`commons.evidence.
     * enforcement`) — a floor on a domain no deployment reads, or a weight key
     * nothing looks up. `rung` is a stored rung, or `pipeline_config` for the
     * pipeline's own config. Empty on a record written before it was tracked.
     */
    inert: InertConfidenceSetting[];
  };
  /** Lists the worker cut at its cap (names of the lists). */
  truncated: string[];
  /** Set when the record itself could not be built. */
  error: string | null;
  /**
   * The central step injections the run folded into its pipeline — applied
   * and skipped, each with why (`authoring.injections`). `null` when the record
   * carries none: a run recorded before injections were tracked, never "none".
   */
  stepInjections: InjectionOutcome[] | null;
}

/** The ingest's own account of a run. Every field optional by source kind. */
export interface IngestReport {
  /** The blocks the dates resolved to — the range actually queried. */
  resolvedFromBlock: number | null;
  resolvedToBlock: number | null;
  rows: number | null;
  /** A sentence naming why the result was empty. Absent when it was not. */
  emptyReason: string | null;
  /** When the address was last active on this chain, if the reason needed it. */
  lastActivityBlock: number | null;
  lastActivityAt: string | null;
  /**
   * Discovery stopped at its page ceiling with more still upstream.
   *
   * The sibling of `emptyReason`, for the case that report did not cover: a run
   * that found SOME and stopped. Asking for five hundred, walking ten pages and
   * finding a hundred and eighty reported a hundred and eighty — which reads as
   * an address with a hundred and eighty transactions, and then as a wallet that
   * went quiet.
   */
  pageCapReached: boolean;
  discoveryFound: number | null;
  discoveryWanted: number | null;
  discoveryPages: number | null;
}

export function appliedConfigurationFromWire(raw: unknown): AppliedConfiguration | null {
  if (!isRecord(raw)) return null;
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  const records = (v: unknown): Record<string, unknown>[] =>
    Array.isArray(v) ? v.filter(isRecord) : [];
  const section = (v: unknown): Record<string, unknown> => (isRecord(v) ? v : {});
  const protocols = section(raw.protocol_overrides);
  const policy = section(raw.confidence_policy);
  return {
    protocols: {
      primed: protocols.primed === true,
      available: protocols.available !== false,
      overrides: records(protocols.overrides).map((o) => ({
        protocolId: asString(o.protocol_id),
        rungs: strings(o.rungs),
        fromPipeline: o.from_pipeline === true,
        fields: strings(o.fields),
      })),
      ignored: records(protocols.ignored).map((o) => ({
        protocolId: asString(o.protocol_id),
        rung: asString(o.rung),
        reason: asString(o.reason),
      })),
    },
    confidence: {
      primed: policy.primed === true,
      available: policy.available !== false,
      rungs: strings(policy.rungs),
      settings: records(policy.settings).map((o) => ({
        path: asString(o.path),
        rung: asString(o.rung),
      })),
      fromPipeline: strings(policy.from_pipeline),
      problems: strings(policy.problems),
      inert: records(policy.inert).map((o) => ({
        path: asString(o.path),
        rung: asString(o.rung),
        reason: asString(o.reason),
      })),
    },
    truncated: [...strings(protocols.truncated), ...strings(policy.truncated)],
    error: asNullableString(raw.error),
    stepInjections: Array.isArray(raw.step_injections)
      ? raw.step_injections.map(outcomeFromWire).filter((o): o is InjectionOutcome => o !== null)
      : null,
  };
}

function ingestReportFromWire(raw: unknown): IngestReport | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const text = (v: unknown): string | null =>
    typeof v === "string" && v.trim() ? v : null;
  const report: IngestReport = {
    resolvedFromBlock: num(r.resolved_from_block),
    resolvedToBlock: num(r.resolved_to_block),
    rows: num(r.rows),
    emptyReason: text(r.empty_reason),
    lastActivityBlock: num(r.last_activity_block),
    lastActivityAt: text(r.last_activity_at),
    pageCapReached: r.discovery_page_cap_reached === true,
    discoveryFound: num(r.discovery_found),
    discoveryWanted: num(r.discovery_wanted),
    discoveryPages: num(r.discovery_pages),
  };
  // An all-empty report is the same as none, and rendering a heading over
  // nothing would imply the ingest declined to answer rather than that this run
  // predates the field.
  // `pageCapReached` is `false` rather than null on every healthy run, so it
  // must not on its own make an otherwise-empty report look present — that
  // would put a heading over nothing on runs predating the field.
  const { pageCapReached, ...rest } = report;
  return pageCapReached || Object.values(rest).some((v) => v !== null) ? report : null;
}

/** The ingest `source` half of a create request. */
export interface RunSourceInput {
  kind: RunSourceKind;
  chainId?: string | null;
  provider?: string;
  count?: number;
  /** `inline`: verbose tx payloads to process verbatim (the fixed-tx test path). */
  payloads?: Record<string, unknown>[];
  /**
   * `latest` / `block_range`: discover the txs involving this address (either
   * side). `pair_swaps`: the DEX pool whose trade feed to pull.
   * `token_transfers`: the ERC-20 CONTRACT whose transfer events to pull — a
   * token is not a party to the transfers of its own supply, so the
   * address-scoped kinds see only the direct calls it receives.
   */
  address?: string | null;
  selector?: string | null;
  onlyMatchingSelector?: boolean;
  /**
   * `pair_swaps`: narrow the pair feed to a comma-separated subset of
   * {@link PAIR_SWAP_TRANSACTION_TYPES}. Omit for every kind.
   */
  transactionTypes?: string | null;
  /** `tx_hashes`: the explicit hashes to fetch (all on `chainId`). */
  txHashes?: string[];
  /** `tx_specs`: explicit `{chainId, txHash}` pairs across chains (cross-chain grouping). */
  txSpecs?: RunTxSpec[];
  /** `block_range`: the inclusive window, by block number… */
  fromBlock?: number | null;
  toBlock?: number | null;
  /** …or by ISO date/timestamp (resolved to blocks via `dateToBlock`). */
  fromDate?: string | null;
  toDate?: string | null;
  /**
   * What the window above MEANS.
   *
   * `exact` (the default, and what a deliberate block-range backfill wants)
   * fetches those blocks. `only_when_large` treats the window as a bound on cost
   * rather than a filter on relevance: the ingest asks how many transactions the
   * address has, and under the run's own `count` cap it drops the window and
   * takes the whole history — cheaper, and it cannot miss the period by a
   * boundary. Over the cap the window is applied, which is where it earns its
   * keep: an exchange hot wallet has 30M transactions and "the newest 100" of
   * those never reaches an older stretch.
   */
  windowMode?: "exact" | "only_when_large" | null;
}

/**
 * Which saved revision a run executes: omitted = the live one (what production
 * runs), `"latest"` = the latest save, published or not (testing a draft), or a
 * revision number.
 */
export type RunPipelineVersion = number | "latest";

export interface CreateRunInput {
  pipelineId?: string;
  pipelineVersion?: RunPipelineVersion | null;
  source: RunSourceInput;
  targetTier?: string | null;
  storeBody?: boolean;
  /**
   * Per-run override of where each transaction body is stored
   * (`customer`/`org`/`tenant`/`user`). Omitted/null inherits the pipeline's
   * authored scope (which defaults to `tenant`).
   */
  bodyScope?: BodyScope | null;
  /** Force-reprocess: re-run every step ignoring the idempotency cache and overwrite stored rows/bodies. */
  overwrite?: boolean;
}

export interface RunJobPage {
  items: RunJobSummary[];
  nextCursor: string | null;
}

export interface ListRunsQuery {
  pipelineId?: string;
  cursor?: string | null;
  limit?: number;
}

export interface RunsSource {
  list(query?: ListRunsQuery): Promise<RunJobPage>;
  get(id: string): Promise<RunJobSummary>;
  create(input: CreateRunInput): Promise<RunJobSummary>;
  /** Convenience alias of `get` for the polling loop (semantic clarity at call sites). */
  poll(id: string): Promise<RunJobSummary>;
  /**
   * Cancel a job that has **not started yet** (`DELETE /runs/{id}`).
   *
   * Deliberately narrow, and the narrowness is the backend's: only a `queued`
   * job can be cancelled, because the worker drains its SQS message
   * independently of the row — a `running` job would keep running while the row
   * claimed otherwise. The API answers a non-queued run with a 400 naming the
   * status, so callers must gate on {@link canCancelRun} and say plainly that a
   * started run cannot be stopped rather than offering a button that fails.
   *
   * Not a delete: the row is the only record that this batch was ever asked
   * for, so it comes back `cancelled` rather than disappearing.
   */
  cancel(id: string): Promise<RunJobSummary>;
}

/**
 * Whether {@link RunsSource.cancel} can still take this run.
 *
 * Mirrors `_cancel` in `pipeline-mgmt-api`: queued only. `running` is the case
 * worth naming in the UI — there is no stop button for it anywhere, and finding
 * that out by starting a 500-transaction run is the expensive way to learn it.
 */
export function canCancelRun(job: RunJobSummary): boolean {
  return job.status === "queued";
}

function producedFromWire(raw: Record<string, unknown>): ProducedTx {
  return {
    pipelineRunId: asNullableString(raw.pipeline_run_id),
    canonicalTxId: asNullableString(raw.canonical_tx_id),
    txHash: asNullableString(raw.tx_hash),
    status: asString(raw.status, "ok"),
    error: asNullableString(raw.error),
  };
}

function jobFromWire(raw: Record<string, unknown>): RunJobSummary {
  return {
    id: asString(raw.id),
    pipelineId: asString(raw.pipeline_id),
    pipelineVersion:
      typeof raw.pipeline_version === "number" && Number.isInteger(raw.pipeline_version) && raw.pipeline_version >= 1
        ? raw.pipeline_version
        : null,
    sourceKind: asString(raw.source_kind, "inline") as RunSourceKind,
    chainId: asNullableString(raw.chain_id),
    provider: asString(raw.provider, "moralis"),
    count: Number(raw.count ?? 0),
    address: asNullableString(raw.address),
    selector: asNullableString(raw.selector),
    onlyMatchingSelector: Boolean(raw.only_matching_selector ?? true),
    transactionTypes: asNullableString(raw.transaction_types),
    txHashes: asStringList(raw.tx_hashes),
    txSpecs: rowsAt(raw, "tx_specs").map((s) => ({
      chainId: asString(s.chain_id ?? s.chainId),
      txHash: asString(s.tx_hash ?? s.txHash),
    })),
    fromBlock: asNumber(raw.from_block),
    toBlock: asNumber(raw.to_block),
    fromDate: asNullableString(raw.from_date),
    toDate: asNullableString(raw.to_date),
    windowMode: (raw.window_mode ?? null) as RunJobSummary["windowMode"],
    targetTier: asNullableString(raw.target_tier),
    storeBody: Boolean(raw.store_body ?? true),
    bodyScope: (raw.body_scope ?? null) as BodyScope | null,
    overwrite: Boolean(raw.overwrite ?? false),
    status: asString(raw.status, "queued") as RunJobStatus,
    requestedBy: asNullableString(raw.requested_by),
    startedAt: asNumber(raw.started_at),
    finishedAt: asNumber(raw.finished_at),
    createdAt: asNumber(raw.created_at),
    leaseExpiresAt: asNumber(raw.lease_expires_at),
    resumeCount: asNumber(raw.resume_count) ?? 0,
    recoveryAttempts: asNumber(raw.recovery_attempts) ?? 0,
    recoveryAfter: asNumber(raw.recovery_after),
    totalCount: Number(raw.total_count ?? 0),
    processedCount: Number(raw.processed_count ?? 0),
    failedCount: Number(raw.failed_count ?? 0),
    error: asNullableString(raw.error),
    deploymentColour: asNullableString(raw.deployment_colour),
    applicationVersion: asNullableString(raw.application_version),
    qaConfig: isRecord(raw.qa_config) ? raw.qa_config : null,
    producedTx: rowsAt(raw, "produced_tx").map(producedFromWire),
    ingestReport: ingestReportFromWire(raw.ingest_report),
    appliedConfiguration: appliedConfigurationFromWire(raw.applied_configuration),
  };
}

/**
 * The ingest kinds whose parameters are fully recoverable from a run summary
 * (so a run can be rerun as-is). `inline` is excluded: the verbose payloads it
 * was started with are never echoed back by the API, so there's nothing to
 * replay from — inline runs must be re-submitted through the dialog.
 */
const REPRODUCIBLE_SOURCE_KINDS: ReadonlySet<RunSourceKind> = new Set<RunSourceKind>([
  "latest",
  "tx_hashes",
  "tx_specs",
  "block_range",
  "pair_swaps",
]);

/** Whether {@link runToCreateInput} can reproduce this run (see {@link REPRODUCIBLE_SOURCE_KINDS}). */
export function canRerun(job: RunJobSummary): boolean {
  return REPRODUCIBLE_SOURCE_KINDS.has(job.sourceKind);
}

/**
 * Build a create request that reproduces an existing run's parameters — the
 * "Rerun" action. Only valid for {@link canRerun} runs; callers must gate on it
 * (an `inline` run's payloads aren't recoverable from its summary).
 */
export function runToCreateInput(job: RunJobSummary): CreateRunInput {
  return {
    pipelineId: job.pipelineId || undefined,
    // The revision the run executed, not whatever is live now — a rerun
    // reproduces the run.
    pipelineVersion: job.pipelineVersion,
    source: {
      kind: job.sourceKind,
      chainId: job.chainId,
      provider: job.provider,
      count: job.count,
      address: job.address,
      selector: job.selector,
      onlyMatchingSelector: job.onlyMatchingSelector,
      transactionTypes: job.transactionTypes,
      txHashes: job.txHashes,
      txSpecs: job.txSpecs,
      fromBlock: job.fromBlock,
      toBlock: job.toBlock,
      fromDate: job.fromDate,
      toDate: job.toDate,
      // The window's MEANING, not just its bounds. Dropping it defaulted the
      // rerun to `exact` server-side, so a `block_range` run created as
      // `only_when_large` — where the window is a cost bound the ingest is free
      // to drop under the count cap — re-ran as a different query while the
      // console called it "the same parameters".
      windowMode: job.windowMode,
    },
    targetTier: job.targetTier,
    storeBody: job.storeBody,
    bodyScope: job.bodyScope,
    overwrite: job.overwrite,
  };
}

function sourceToWire(source: RunSourceInput): Record<string, unknown> {
  const wire: Record<string, unknown> = { kind: source.kind };
  if (source.chainId != null && source.chainId !== "") wire.chain_id = source.chainId;
  if (source.provider) wire.provider = source.provider;
  if (source.count != null) wire.count = source.count;
  if (source.payloads != null) wire.payloads = source.payloads;
  if (source.address != null && source.address !== "") wire.address = source.address;
  if (source.selector != null && source.selector !== "") wire.selector = source.selector;
  if (source.onlyMatchingSelector != null) wire.only_matching_selector = source.onlyMatchingSelector;
  if (source.transactionTypes != null && source.transactionTypes !== "")
    wire.transaction_types = source.transactionTypes;
  if (source.txHashes != null) wire.tx_hashes = source.txHashes;
  if (source.txSpecs != null)
    wire.tx_specs = source.txSpecs.map((s) => ({ chain_id: s.chainId, tx_hash: s.txHash }));
  if (source.fromBlock != null) wire.from_block = source.fromBlock;
  if (source.toBlock != null) wire.to_block = source.toBlock;
  if (source.fromDate != null && source.fromDate !== "") wire.from_date = source.fromDate;
  if (source.toDate != null && source.toDate !== "") wire.to_date = source.toDate;
  if (source.windowMode) wire.window_mode = source.windowMode;
  return wire;
}

export function createApiRunsSource(client: ApiClient): RunsSource {
  const runPath = (id: string) => `/runs/${encodeURIComponent(id)}`;

  // Run status is live (a worker mutates it out-of-band), so reads must always
  // hit the network — the 30s GET cache would otherwise freeze the poll loop on
  // a stale "queued" snapshot and make Refresh a no-op. `cacheTtlMs: 0` opts
  // these reads out of caching.
  const get = async (id: string): Promise<RunJobSummary> =>
    jobFromWire(asRecord(unwrapData(await client.get<unknown>(runPath(id), { cacheTtlMs: 0 }))));

  return {
    async list(query?: ListRunsQuery): Promise<RunJobPage> {
      const page = unwrapData(
        await client.get<{ items?: unknown; next_cursor?: string | null }>("/runs", {
          query: {
            pipeline_id: query?.pipelineId,
            cursor: query?.cursor ?? undefined,
            limit: query?.limit,
          },
          cacheTtlMs: 0,
        }),
      );
      return {
        items: rowsFromWire(page?.items).map(jobFromWire),
        nextCursor: page?.next_cursor ?? null,
      };
    },
    get,
    poll: get,
    async create(input: CreateRunInput): Promise<RunJobSummary> {
      const body: Record<string, unknown> = { source: sourceToWire(input.source) };
      if (input.pipelineId) body.pipeline_id = input.pipelineId;
      if (input.pipelineVersion != null) body.pipeline_version = input.pipelineVersion;
      if (input.targetTier != null) body.target_tier = input.targetTier;
      if (input.storeBody != null) body.store_body = input.storeBody;
      if (input.bodyScope != null) body.body_scope = input.bodyScope;
      if (input.overwrite != null) body.overwrite = input.overwrite;
      const created = jobFromWire(asRecord(unwrapData(await client.post<unknown>("/runs", body))));
      // No cache to drop: every read above is `cacheTtlMs: 0` because run status
      // is mutated out-of-band by a worker. The `client.invalidate("/runs")` that
      // used to sit here was doubly dead — the list is never cached, AND it is
      // read with query params, so the exact key it deleted was not the key a
      // cached read would have written. If these reads are ever given a TTL, the
      // correct call is `invalidateMatching("/runs")`.
      return created;
    },
    async cancel(id: string): Promise<RunJobSummary> {
      const cancelled = jobFromWire(
        asRecord(unwrapData(await client.delete<unknown>(runPath(id)))),
      );
      // Uncached, as above.
      return cancelled;
    },
  };
}
